/**
 * TranscriptEvidencePanel (TASK-618)
 *
 * Evidence variant of the workspace's left/document panel for MEETING-sourced
 * proposals: fetches the utterance window around the proposal's source quote
 * and renders the excerpt with the source utterance highlighted.
 *
 * Moved out of `kyc/workspace/` in the TASK-683 review: the provenance chain opens it too
 * now (every cited claim in the graph is meeting-sourced), and a component two features
 * share does not belong inside one of them.
 */

import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { httpClient } from '../../lib/httpClient';
import { logger } from '../../utils/logger';

interface ExcerptUtterance {
  seq: number;
  speaker_id: string | null;
  text: string;
  start_ms: number | null;
  matched: boolean;
}

interface TranscriptExcerpt {
  meeting_id: string;
  matched_seq: number | null;
  utterances: ExcerptUtterance[];
}

interface TranscriptEvidencePanelProps {
  meetingId: string;
  quote?: string;
  utteranceSeq?: number;
  onClose: () => void;
}

const msToClock = (ms: number | null): string => {
  if (ms == null) return '';
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const TranscriptEvidencePanel: React.FC<TranscriptEvidencePanelProps> = ({
  meetingId,
  quote,
  utteranceSeq,
  onClose,
}) => {
  const [excerpt, setExcerpt] = useState<TranscriptExcerpt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const matchedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params: Record<string, string | number> = {};
    if (utteranceSeq != null) params.seq = utteranceSeq;
    else if (quote) params.quote = quote;
    httpClient
      .get<TranscriptExcerpt>(`/api/meeting/${meetingId}/transcript-excerpt`, { params })
      .then(setExcerpt)
      .catch((err) => {
        logger.error('[TranscriptEvidencePanel] excerpt fetch failed', err);
        setError('Could not load the transcript excerpt');
      });
  }, [meetingId, quote, utteranceSeq]);

  // Scroll + pulse the source utterance once loaded.
  useEffect(() => {
    matchedRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [excerpt]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        data-testid="transcript-evidence-panel"
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
          <MessageSquare className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-gray-900">Transcript evidence</span>
          <button onClick={onClose} className="ml-auto p-1 hover:bg-gray-100 rounded" title="Close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-4 py-3 space-y-2">
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!error && !excerpt && <p className="text-sm text-gray-500">Loading excerpt…</p>}
          {excerpt?.utterances.map((u) => (
            <div
              key={u.seq}
              ref={u.matched ? matchedRef : undefined}
              className={`text-sm rounded-md px-3 py-2 ${
                u.matched
                  ? 'bg-amber-100 border border-amber-300 animate-pulse [animation-iteration-count:3]'
                  : 'bg-gray-50'
              }`}
            >
              <span className="text-xs text-gray-500 mr-2">
                {u.speaker_id || 'Speaker'}
                {u.start_ms != null && ` · ${msToClock(u.start_ms)}`}
              </span>
              <span className={u.matched ? 'font-medium text-gray-900' : 'text-gray-700'}>
                {u.text}
              </span>
            </div>
          ))}
          {excerpt && excerpt.matched_seq == null && (
            <p className="text-xs text-gray-500 italic">
              Source utterance not found verbatim — showing the start of the transcript.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranscriptEvidencePanel;
